import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { httpRequest, httpRequestLimitNums } from '@/request';
import {
  browserPushUrl,
  cache,
  CountryCode,
  isMobile,
  getLANG,
  isEmpty,
  isBroswer,
  formatProductName,
  isAvailableForSale,
  isSameProductName,
  uniq,
  debounce,
} from '@/utils';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { SERVER, BASE_STORE_URL } from '@/request/config';
import qs from 'qs';
import { _8kLivePid } from '@/utils/configs/8kLive.config';
import { getExtraMenusConfig } from '@/pages/product/components/ProductOverview/data.config';
import ExtraBindServiceConfig from '@/components/ExtraBindService/data.config';
import { EcommerceTracker, ProductTracker } from '@/utils/Tracker';
import ThirdPartyPlatformConfig from '@/utils/configs/ThirdPartyPlatform.config';
import {
  getBatteryOrChargeHubName,
  isNeedHidePriceProduct,
  isPreSaleCommodity,
  TshirtPids,
} from '@/utils/configs/product.config';
import { getVersionConfig, getVersionLabel } from '@/pages/product/config/ProductVersion.config';
import { getCommodityItemLabel } from '@/pages/product/config/productLabel.config';
import cloudProductConfig, {
  setCommodityToCloudConfig,
  getCloudCid,
  getCloudProductConfig,
} from '@/components/ExtraBindService/CloudServices.config';
import axios from 'axios';
import { customRowConfig, customColConfig } from '@/pages/product/components/ProductDiff/data.config';
import { formatPriceThousands } from '@/utils/configs/price.config';
import CustomSkinHelper from '@/pages/product/components/CustomSkin/helper';
import StoreBase from './Base';
import { supportedAdapterIdMap } from '@/utils/configs/adapterIds.configs';
import { stashCidInSessionStorage } from '@/pages/relatedAccessories/utils';
import { getX3X4AccessoriesProductIds } from '@/service/accessories';
import { isDuringNowAfterDate } from '@/utils/date';
import panoramicDisplayConfig from '@/pages/product/components/PanoramicDisplay/config.ts';

const LANG = getLANG();

/** 初始化获取自定义套餐，如果更换国家则清空之前的数据 */
function restorePersonalizedData() {
  let data = cache.get('personalized_data') || [];

  try {
    data = JSON.parse(data);
    data.forEach((o) => {
      const disable = o.CountryCode !== CountryCode || new Date().getTime() > (o.endTime || 0);
      if (disable) {
        data = [];
        cache.set('personalized_data', JSON.stringify([]));
      }
    });
  } catch (e) {
    data = [];
    cache.set('personalized_data', JSON.stringify([]));
  }
  return data;
}

/*
 *
 * @desc: 切换套餐修改Meta标签信息（facebook Microdata Tags）
 * @param {cid} String 套餐id
 * @param {productDetail} Object 用于获取套餐信息判断该套餐销售状态
 * @time: 2021/10/21 3:11 下午
 *
 */
function handleHeaderMetaAttrValue({ cid, productDetail }) {
  if (isEmpty(productDetail)) return;
  if (typeof window === 'undefined') return;
  const { commodities, id: pid } = productDetail;
  if (!commodities) return;
  const commodity = commodities.filter((o) => o.id === cid)[0];
  if (!commodity) return;
  const metaGroup = [...document.querySelectorAll('meta')];
  metaGroup.forEach((o) => {
    switch (o.getAttribute('property')) {
      case 'product:availability': {
        const availability = commodity.sale_state === 0 ? 'in stock' : 'out of stock';
        o.setAttribute('content', availability);
        break;
      }
      case 'product:price:amount':
        o.setAttribute('content', commodity.price.amount);
        break;
      case 'product:price:currency':
        o.setAttribute('content', commodity.price.currency);
        break;
      case 'product:retailer_item_id':
        o.setAttribute('content', `Insta360_${pid}_${commodity.id}`);
        break;
      case 'product:item_group_id':
        o.setAttribute('content', `Insta360_${pid}`);
        break;
      default:
        break;
    }
  });
}

class State extends StoreBase {
  constructor(props) {
    super(props);
    makeObservable(this);
    this.LANG = props.area.LANG || {};
    this.insrc = props.app.insrc;
    this.country = props.area.country;
    if (isBroswer) {
      this.careplus_cost_cids = ExtraBindServiceConfig.getCarePlusCostCids([], true);
    }
  }

  /**
   *  @description 存储carePlus的置换费，用于以下两个场景
   * 1. 产品页购物车套餐简介
   * 2. 产品页/购物车页的care和延保的learnMore弹窗
   */

  @observable
  carePlusPrice = new Map();

  // 产品页的购物车栏元素
  @observable
  eleStickyTabBar = null;

  // 产品页购物车栏元素是否显示,PC端首屏默认为true、移动端默认为false
  @observable
  eleStickyTabBarVisible = !this.root.app.isMobile;

  @observable
  updateState = false;

  // 用户分销赠品
  @observable
  resellerGiftsList = [];

  // 用户分销赠品 key为套餐，key=‘default' 默认赠品/针对分销商设置全部套餐共享赠品
  @observable
  productGiftsMap = {};

  // 活动页套餐赠品
  @observable
  commodityGiftsMap = {};

  @observable
  commodityDisplays = null;

  @observable
  products = [];

  @observable
  productModularOverview = {};

  @observable
  bannersMap = {
    store: {
      list: [],
    }, // 桌面端
    store_m: {
      list: [],
    }, // 移动端
    store_m_campaign: {
      list: [],
    }, // 移动端 - 活动
    store_m_programs: {
      list: [],
    }, // 移动端 - 其他项目
    store_programs: {
      list: [],
    }, // 桌面端 - 其他项目
    store_affiliate: {
      list: [],
    }, // 分销商活动banner
  };

  @observable
  accessoryCommodityIdList = null;

  // remove
  @observable
  subscribe_commodity = null;

  @observable
  productDetail = {};

  // 如果在产品页面上需要展示其他的产品信息，不妨碍productDetail的前提下可以将数据存储于此
  @observable
  otherProductDetail = null;

  @observable
  productCommodityRecommend = [];

  @observable
  productReviewList = [];

  /** desc:productReviewInfo的属性
   *  productId  当前产品ID
   *  reviewCountSum 评论总数
   *  reviewRateAvg 评论星级均数
   *  rateMap 评论星级分布
   */
  @observable
  productReviewInfo = {};

  // 产品注意事项，type:0:productDetail.faqMores 中存在历史数据,type1:相机类型，type2:配件类型
  @observable
  productImportantNotes = { type: 0, data: [] };

  @observable
  curSelectCommodityId = -1;

  @observable
  curSelectCommodityCount = 1;

  @observable
  resellerProductGift = [];

  @observable
  subscribeCommodityModalVisible = false;

  @observable
  subscribeCommodity = {
    commodityId: '',
    productId: '',
    disabledToggleScroll: false,
  };

  @observable
  personalizedData = isBroswer && restorePersonalizedData();

  @observable
  isSelectPersonalized = false;

  @computed
  get curPersonalizedItems() {
    const { productDetail = {}, personalizedData } = this;
    const { id: pid } = productDetail ?? {};
    return !isEmpty(personalizedData) ? personalizedData.filter((o) => o.pid === pid)[0] : {};
  }

  // 当前产品额外套餐列表
  @observable
  extraBindService = [];

  // 当前加入产品页购物车的电池
  @observable
  extraBattery = null;

  // 当前加入产品页购物车的定制, Flow || GO3 || GO3S
  @observable
  extraCustomSkin = [];

  /** 产品页中展示额外服务，专门为云存储服务的，不走增值服务逻辑 */
  @observable
  extraService = [];

  @observable
  overviewType = 'default'; // default || onerLeica || onex2Naruto || onex2Sasuke

  @observable
  overviewUrl = '';

  /** 包装清单列表数据 -  相机类型：type-1 pointGroup / commodityGroup，配件类型：type-2 accessoryDiffData */
  @observable
  packingListPoints = {
    type: 'packingListPoints',
    productPackListType: 1,
    title: LANG['productNew.inTheBox'],
    pointGroup: null,
    commodityGroup: null,
    accessoryDiffData: '', // 配件类型包装清单数据
  };

  /** 产品参数数据-配件专用，相机使用 */
  @observable
  productParameterInfos = [];

  // 服务（延保、care）
  @observable
  InsuranceService = {
    serial: cache.get('i_s_d_s') || '', // 用户输入序列号 insurance_service_device_serial
    purchaseTime: '', // 校验LINK的购买时间，验证字段复用serialChecked、serialCheckedMsg
    selectProtocol: false, // 是否同意协议。
    serialChecked: false, // 序列号验证是否通过
    serialCheckedMsg: '', // 错误信息
    modalVisible: false, // 校验序列号弹窗
    stepStack: [1], // 弹窗展示的步骤
  };

  @computed
  get curSelectCommodity() {
    try {
      // 如果productDetail存在当前选择的套餐id则从此处取数据，否则从otherProductDetail取数据
      if (this.productDetail?.['commodities']?.some((c) => c.id === this.curSelectCommodityId)) {
        return this.productDetail['commodities'].filter((o) => o.id === this.curSelectCommodityId)[0] ?? {};
      } else if (this.otherProductDetail) {
        return this.otherProductDetail.commodities.find((o) => o.id === this.curSelectCommodityId) ?? {};
      } else {
        return {};
      }
    } catch (e) {
      return {};
    }
  }

  @computed
  get curCommodityList() {
    return this.getCurCommodityList(this.curSelectCommodityId);
  }

  @action
  getCurCommodityList = (targetCid) => {
    if (this.productDetail?.VersionConfig?.type === 'group') {
      // 获取当前包含当前套餐的versionList，获取所有属性纬度的数据
      const versionList = this.productDetail.VersionConfig?.groupList.filter((d) => d.cids.includes(targetCid));
      // 套餐id Map, 统计每个versionLis中cid出现的次数
      const cidsMap = new Map();
      versionList.forEach((o) => {
        o?.cids?.forEach((cid) => {
          cidsMap.set(cid, (cidsMap.get(cid) || 0) + 1);
        });
      });
      // 只留下所有versionList的交集, 这些交集就代表着满足所有条件的套餐数据
      const uniteArr = [];
      for (const [key, count] of cidsMap) {
        if (count === versionList.length) {
          uniteArr.push(key);
        }
      }
      // 通过cid取得套餐
      const commodities = this.productDetail?.commodities?.filter((commodity) => {
        if (uniteArr.includes(commodity.id)) {
          return true;
        }
        return false;
      });
      return commodities ?? [];
    } else {
      return this.productDetail?.commodities ?? [];
    }
  };

  @computed
  get curProductCommodityRecommend() {
    if (this.productCommodityRecommend) {
      if (this.productCommodityRecommend.length > 0) {
        const recommend = this.productCommodityRecommend.filter((o) => o.commodityId === this.curSelectCommodityId)[0];
        return recommend?.['commodities'] ?? null;
      }
    }
    return null;
  }

  // 计算产品页临时购物车总金额
  @computed
  get productTotalPrice() {
    // 货币从套餐价格配置里取，需要考虑套餐价格与当前地区货币不一致的情况；
    // 例如： 日本地区售卖美元产品，价格需要显示美元。
    const signal = this.curSelectCommodity?.price?.signal;
    const currency = this.curSelectCommodity?.price?.currency;

    // 常规套餐金额
    let priceCommodity = 0;
    let priceCommodityOrigin = 0;

    // 额外套餐（定制）
    let priceExtraCustomSkin = 0;
    let priceExtraCustomSkinOrigin = 0;

    // 额外套餐金额
    let priceExtraBindService = 0;
    let priceExtraBindServiceOrigin = 0;

    // 电池加购金额
    let priceBattery = 0;
    let priceBatteryOrigin = 0;

    // 推荐配件金额
    let priceCommodityRecommend = 0;
    let priceCommodityRecommendOrigin = 0;

    const {
      isSelectPersonalized,
      curPersonalizedItems,
      curSelectCommodity,
      curSelectCommodityCount,
      extraBindService,
      extraService,
      extraBattery,
      extraCustomSkin,
    } = this;
    if (isEmpty(curSelectCommodity)) return { amount: 0, signal, currency };
    if (isSelectPersonalized && !isEmpty(curPersonalizedItems)) {
      const commodities = curPersonalizedItems.commodities;
      commodities?.forEach((o) => {
        // 自定义套餐数量跟着最外层 curSelectCommodityCount
        priceCommodity += (o.price.amount ?? 0) * curSelectCommodityCount;
        if (o.origin_price && o.origin_price.amount) {
          priceCommodityOrigin += o.origin_price.amount * curSelectCommodityCount;
        } else {
          priceCommodityOrigin += o.price.amount * curSelectCommodityCount;
        }
      });
    } else {
      const { isOutOfStock, origin_price, price } = curSelectCommodity;
      priceCommodity =
        ((isOutOfStock ? origin_price?.amount ?? price?.amount ?? 0 : price?.amount ?? 0) ?? 0) *
        curSelectCommodityCount;

      if (origin_price && origin_price.amount) {
        priceCommodityOrigin = origin_price?.amount * curSelectCommodityCount;
      } else {
        priceCommodityOrigin = price?.amount * curSelectCommodityCount;
      }
    }

    if (extraBindService) {
      extraBindService.forEach((o) => {
        const c = o.commodity;
        priceExtraBindService += c.price.amount * c.count;
        if (!c.origin_price) {
          priceExtraBindServiceOrigin += c.price.amount * c.count;
        } else {
          priceExtraBindServiceOrigin += c.origin_price.amount * c.count;
        }
      });
    }

    /** 产品页额外服务 - 目前只有云服务 */
    if (extraService) {
      extraService.forEach((o) => {
        const c = o.commodity;
        if (c) {
          priceExtraBindService += c.price?.amount * c?.count || 0;
          if (!c.origin_price) {
            priceExtraBindServiceOrigin += c.price?.amount * c?.count || 0;
          } else {
            priceExtraBindServiceOrigin += c.origin_price?.amount * c?.count || 0;
          }
        }
      });
    }

    if (extraBattery) {
      priceBattery = extraBattery.price.amount * extraBattery.count;
      priceBatteryOrigin = priceBattery;
      if (extraBattery.origin_price && extraBattery.origin_price.amount) {
        priceBatteryOrigin = extraBattery.origin_price.amount * extraBattery.count;
      }
    }

    const customType = CustomSkinHelper.getCustomType({ commodityId: curSelectCommodity.id });

    if (extraCustomSkin?.length > 0 && !customType) {
      // 定制配件页不累加，从主套餐加
      // 产品页加购定制需要累加
      extraCustomSkin.forEach((o) => {
        priceExtraCustomSkin += o.price.amount * o.count;
        if (o.origin_price && o.origin_price.amount) {
          priceExtraCustomSkinOrigin += o.origin_price.amount * o.count;
        } else {
          priceExtraCustomSkinOrigin += o.price.amount * o.count;
        }
      });
    }

    const price = {
      amount: priceCommodity + priceExtraBindService + priceBattery + priceCommodityRecommend + priceExtraCustomSkin,
      amountOrigin:
        priceCommodityOrigin +
        priceExtraBindServiceOrigin +
        priceBatteryOrigin +
        priceCommodityRecommendOrigin +
        priceExtraCustomSkinOrigin,
      currency,
      signal,
      hasDiscount: false,
    };
    if (price.amountOrigin === price.amount) {
      price.amountOrigin = 0;
    } else if (!curSelectCommodity.isOutOfStock) {
      price.hasDiscount = true;
    }
    if (isBroswer && this.root.app.G_IsProductPage && isNaN(price.amount)) {
      try {
        // 价格计算异常，上报异常
        const collectData = {
          price,
          isSelectPersonalized,
          curPersonalizedItems,
          curSelectCommodity,
          curSelectCommodityCount,
          extraBindService,
          extraBattery,
          extraCustomSkin,
          insrc: this.insrc,
          url: this.root.app?.urlFull,
          PodEnv: this.root.app?.PodEnv,
        };

        this.root.auth.dataCollect({
          event: 'ProductPagePriceError',
          data: JSON.stringify(collectData),
        });
        console.error('ProductPagePriceError', JSON.stringify(collectData));
      } catch (e) {
        console.error('ProductPagePriceError', JSON.stringify(e));
      }
    }
    return price;
  }

  // 获取产品页临时购物车所有套餐
  @computed
  get productCarts() {
    let commodities = [];
    const {
      isSelectPersonalized,
      curPersonalizedItems,
      curSelectCommodity,
      curSelectCommodityCount,
      extraBindService,
      extraBattery,
      extraCustomSkin,
      extraService,
      productDetail,
    } = this;
    if (isSelectPersonalized && !isEmpty(curPersonalizedItems)) {
      commodities = [...curPersonalizedItems.commodities];
    } else {
      curSelectCommodity.product_name = productDetail?.info?.name;
      curSelectCommodity.count = curSelectCommodityCount;
      curSelectCommodity.groupType = 'main';

      // 定制贴默认数量为0，最终加购数量由localstorage缓存存储数量决定
      if (curSelectCommodityCount > 0) {
        commodities = [curSelectCommodity];
      }
    }

    // 云存
    if (extraService) {
      extraService.forEach((o) => {
        if (o?.commodity?.count > 0) {
          commodities.push(o.commodity);
        }
      });
    }

    // 虚拟增值服务
    if (extraBindService) {
      extraBindService.forEach((o) => {
        if (o?.commodity?.count > 0) {
          commodities.push(o.commodity);
        }
      });
    }

    // 电池
    if (extraBattery && extraBattery.count > 0) {
      commodities.push(extraBattery);
    }

    // 定制贴
    const customType = CustomSkinHelper.getCustomType({ commodityId: curSelectCommodity.id });
    if (extraCustomSkin?.length > 0 && !customType) {
      commodities = commodities.concat([...extraCustomSkin]);
    }

    return commodities;
  }

  /**
   * @description 产品页购物车栏的展示和隐藏
   * @param {boolean} toggle
   * @param {string{'smooth'|'instant'}} behavior smooth滑动 、 instant立刻
   *  */
  @action
  toggleStickyTabBar = (toggle = false, behavior = 'smooth') => {
    if (!this.eleStickyTabBar) return;

    if (behavior === 'instant') {
      this.eleStickyTabBar.style.transition = 'none';
      setTimeout(() => {
        this.eleStickyTabBar.style.transition = 'all 0.3s ease-in-out';
      }, 100);
    }

    if (toggle) {
      this.eleStickyTabBarVisible = true;
      this.eleStickyTabBar.style.transform = `translate3d(0,0,0)`;
      this.eleStickyTabBar.style.opacity = 1;
    } else {
      this.eleStickyTabBarVisible = false;
      this.eleStickyTabBar.style.transform = `translate3d(0,100%,0)`;
      this.eleStickyTabBar.style.opacity = 0;
    }
    this.root.meta.changePageToolBarPosition();
  };

  // 获取产品页评论简介
  @action
  fetchReviewStract = () => {
    if (!this?.productDetail?.id) return;
    // 获取评论总数
    // const fetchTotalInfoUrl = SERVER.API_SERVER_BASE + '/review/getProductReviewRateInfo';
    // 获取评论星级信息
    const fetchRateInfoUrl = SERVER.API_SERVER_BASE + '/review/getProductReviewRateLevelInfo';

    return httpRequest({
      url: fetchRateInfoUrl,
      params: { productId: this?.productDetail?.id },
    }).then((res) => {
      if (res?.code === 0) {
        this.productReviewInfo.rateMap = res?.data?.rateLevelInfoMap;
        this.productReviewInfo.reviewCountSum =
          Object.values(res?.data?.rateLevelInfoMap ?? {}).reduce((sum, d) => sum + Number(d), 0) ??
          this.productReviewInfo.reviewCountSum ??
          0;
      } else console.log('error not get commentInfo');
      return [this.productReviewInfo, this.productReviewInfo.rateMap];
    });
  };

  // 获取产品页的评论内容
  @action
  fetchProductReviewInfo = (data, cb) => {
    // 处理localStorage中的点赞信息
    const handleData = (data) => {
      if (isEmpty(data)) return data;
      try {
        const timestamp = new Date().valueOf(); // 更换数据后，由于map队列中key值不变，导致react不更新节点
        const likeData = new Set(JSON.parse(cache.get(`review_like`) || '[]'));
        return data?.map((item) => {
          const { reviewNumber } = item;
          if (cache.get(`give_a_review_live_${reviewNumber}`) === '1' || likeData.has(reviewNumber)) {
            if (cache.get(`give_a_review_live_${reviewNumber}`) === '1') {
              cache.set('review_like', JSON.stringify([...likeData, reviewNumber]));
              cache.remove(`give_a_review_live_${reviewNumber}`);
            }
            item.isLike = true;
          }

          item.timestamp = timestamp;
          return item;
        });
      } catch (e) {
        return data;
      }
    };

    const params = {
      pageSize: isMobile() ? 10 : 5,
      ...data,
      productId: this.productDetail.id,
    };
    const url = SERVER.API_SERVER_BASE + '/review/listReviews';
    return httpRequest({
      url,
      params,
    }).then((res) => {
      if (res.code === 0) {
        if (!isMobile()) {
          this.productReviewList = handleData(res.data?.list ?? []);
        } else {
          if (isEmpty(this.productReviewList) || data.pageNumber === 1) {
            this.productReviewList = handleData(res.data?.list ?? []);
          } else {
            this.productReviewList.push(...handleData(res.data?.list ?? []));
          }
        }

        cb && cb(res.data);
      } else this.productReviewList = [];
    });
  };

  @action
  setStateValue = (key, value, firstEmit) => {
    this[key] = value;
    if (key === 'curSelectCommodityId') {
      // 选择套餐切换对应产品详情图
      const overViewType = getExtraMenusConfig({
        isMobile,
        LANG: this.LANG,
        pid: this.productDetail.id,
        cid: value,
        countryCode: this.root.area.country.code,
        commoditiesList: this.productDetail.commodities,
        isTargetingResult: this.root.abtest?.isTargetingResult,
        isX4DivingOriginal: this.root.abtest?.isX4DivingOriginal,
      });
      if (overViewType) {
        const extra = overViewType.find(
          (d) =>
            (d.specialEnableCountry ? d.specialEnableCountry?.includes(CountryCode) : true) &&
            d.commodities?.includes(value)
        );
        this.overviewType = extra?.type || 'default';
      }

      if (firstEmit) {
        EcommerceTracker.viewItem({ productDetail: this.productDetail, commodityId: value });
      } else {
        EcommerceTracker.selectItem({ productDetail: this.productDetail, commodityId: value });
      }

      if (isBroswer) {
        this.root.app.trackFb('ViewContent', { productId: value });
        this.root.app.trackTiktok('ViewContent', {
          curSelectCommodityCount: this.curSelectCommodityCount,
          curSelectCommodity: this.curSelectCommodity,
          productDetail: this.productDetail,
        });
      }
      this.initProductExtraBindService();
      handleHeaderMetaAttrValue({ cid: value, productDetail: this.productDetail });
    }
    if (key === 'curSelectCommodityCount') {
      this.setExtraBindServiceCount(value);
    }
  };

  @action
  fetchProduct = (callback) => {
    const search = this.root.app.urlQuery;

    // productDetail 已在服务端做了一层数据过滤
    let product = this.productDetail;

    // 排序、筛选、套餐子项label处理 产品数据通用逻辑
    this.handleCommonProductDetail(product);

    // 处理版本套餐VersionConfig
    this.initProductMenuVersion(product);

    // 配置当前页面的curSelectCommodityId
    if (search && search.c && !isNaN(parseInt(search.c))) {
      // 如果进入页面时就带了套餐参数，则自动定位选择curSelectCommodityId，只有正常销售状态才才置顶
      // window.history.pushState({}, null, handleQueryParams('c'));
      const cid = parseInt(search.c);
      const c = product.commodities.filter((o) => o.id === cid)[0];

      if (c) {
        // 不做置顶 : 1.配置内电池和充电座 2.go3单相机
        const batteryOrChargeHubName = getBatteryOrChargeHubName({ cid, LANG: this.LANG });
        if (c.stock > 0 && c.sale_state === 0 && !batteryOrChargeHubName && product.id !== 475) {
          product.commodities = product.commodities.filter((o) => o.id !== cid);
          product.commodities.unshift(c);

          /**
           *   @description 如果产品有规格的话 同样在有地址带有套餐参数c=xxx的情况下需要置顶该套餐
           *    这个置顶排序是发生在 product.VersionConfig.groupList 中的
           */
          if (product.VersionConfig?.type === 'group') {
            product.VersionConfig.groupList = product.VersionConfig.groupList.map((glist) => {
              if (glist?.cids?.includes(cid)) {
                glist.list = glist.list.filter((o) => o.id !== cid);
                glist.list.unshift(c);
              }
              return glist;
            });
          }
        }

        this.setStateValue('curSelectCommodityId', cid, true);
      } else {
        this.setStateValue('curSelectCommodityId', product.commodities[0]['id'], true);
      }
    } else {
      this.setStateValue('curSelectCommodityId', product.commodities[0]['id'], true);
    }

    // 初始化虚拟服务选项
    this.initProductExtraBindService(product);

    // 如果是需要内存卡选项的产品需要做聚合处理。目前仅有GO2
    // product.id === 14 && this.initRelativeCommodity(product);

    // 处理overView详情数据
    product.info.overview = product.info.overview
      ? product.info.overview.replace(/&nbsp;/g, '')
      : product.info.overview;

    // 如果之前有上个产品的数据需清空
    this.handleInsuranceService('reset');

    this.productDetail = product;
    this.productImportantNotes.data = product.faqMores;
    this.accessoryCommodityIdList = product.accessories_commodity ? product.accessories_commodity.split(',') : [];

    this.setStateValue('extraRecommendCommodities', []);

    if (callback) {
      callback(product);
    }
  };

  /**
   * 除了产品页本身获取productDetail外，可以传入id查询其他的产品信息
   */
  @action
  fetchOtherProductDetail = (id, callback) => {
    if (!this.otherProductDetail || this.otherProductDetail?.id !== id) {
      const url = SERVER.API_SERVER_PRODUCT + '/product/getInfo';
      httpRequest({
        url,
        params: { id, insrc: this.insrc },
      }).then(({ code, data }) => {
        if (code === 0) {
          this.handleCommonProductDetail(data.product);
          this.otherProductDetail = data.product;
          callback?.(data?.product);
        }
      });
    } else {
      callback?.(this.otherProductDetail);
    }
  };

  /**
   * 处理产品信息的共同逻辑 筛选&排序&套餐标签
   */
  @action
  handleCommonProductDetail = (product) => {
    if (!product) return;
    const search = this.root.app.urlQuery;
    const insrc = this.insrc;

    // 1. 筛选套餐，部分分销商不展示
    // 中国大陆VR看房-ONE X&Nano S 58安居客
    if (product.id === 225) {
      const affiliateSKU = [956, 957, 1204, 1205, 2902, 3682];
      if (insrc && ['INR5IM7'].includes(insrc)) {
        product.commodities = product.commodities.filter((o) => affiliateSKU.includes(o.id));
      } else {
        product.commodities = product.commodities.filter((o) => !affiliateSKU.includes(o.id));
      }
    }

    if (product.id === 21) {
      // 隐藏海外oneR房产套餐
      if (insrc && ['INRUWUJ', 'INRJZGZ', 'INRM1IZ'].includes(insrc)) {
        product.commodities = product.commodities.filter((o) => ![1075].includes(o.id));
      }
      // 隐藏海外ONE X2 VR房产套餐
      if (insrc && ['INRRMKQ'].includes(insrc) && ['721', '1454'].includes(search.c)) {
        product.commodities = product.commodities.filter((o) => [parseInt(search.c)].includes(o.id));
      }
    }

    // 2. 套餐排序 将不可售套餐移到底部，遍历处理套餐数据
    const tempCommoditiesInSale = [];
    const tempCommoditiesOutSale = [];
    product.commodities.forEach((o) => {
      const name = formatProductName(o.info?.name);
      o.versionName = o.info?.name?.includes('||') ? name.versionSplit[0] : undefined;
      o.commodityName = name.versionSplit?.[1];
      o.displays?.forEach((display) => (display.productId = product.id));
      o.isOutOfStock ? tempCommoditiesOutSale.push(o) : tempCommoditiesInSale.push(o);

      // 套餐不可售状态下，只显示原价，不显示折扣价
      if (o.isOutOfStock && o.origin_price) {
        o.price = { ...o.origin_price };
        delete o.origin_price;
      }
    });
    product.commodities = [...tempCommoditiesInSale, ...tempCommoditiesOutSale];

    // 3. 处理套餐标签
    this.handleProductLabels(product);
  };

  /*
   *  导航栏切换相同产品下不同套餐, ?c=xxx
   */
  @action
  handleProductQueryParams = () => {
    const search = qs.parse(window.location.search.substring(1));
    if (!search || !search?.c || isNaN(parseInt(search.c))) return;
    const cid = parseInt(search.c);
    const c = this.productDetail.commodities.filter((o) => o.id === cid)[0];
    if (c) {
      this.setStateValue('curSelectCommodityId', cid);
    }
  };

  /** 处理套餐标签，无库存标签、存在减价则自动引入折扣标签、后台配置标签 */
  handleProductLabels = (product) => {
    const { campaignWrap } = this.root.app;
    const { country } = this.root.area;
    product.commodities.forEach((commodity) => {
      // 如果是4个一行的Tshirt不显示任何标签 @Iris
      if (TshirtPids.includes(commodity.product)) {
        commodity.tagInfosGroup = [];
        return;
      }

      let tagInfosGroup = commodity.commodity_tag_infos ?? [];
      const LABEL = getCommodityItemLabel({ LANG: this.LANG });

      // 无库存不展示任何标签（ABT全量），以前展示 LABEL.outOfStock
      if (commodity.isOutOfStock) {
        commodity.tagInfosGroup = [];
        return;
      }

      // 减价标签
      if (!isEmpty(commodity.origin_price) && commodity.origin_price?.amount > commodity.price?.amount) {
        tagInfosGroup?.push({
          ...LABEL.savePrice,
          tagContent: LABEL.savePrice.tagContent?.replace(
            '__price__',
            formatPriceThousands({
              price: commodity.origin_price.amount - commodity.price.amount,
              signal: commodity.price.signal,
              currency: commodity.price.currency,
              country,
            })
          ),
        });
      }

      tagInfosGroup = campaignWrap?.getTagInfosGroup({ commodity, tagInfosGroup, product });

      commodity.tagInfosGroup = tagInfosGroup;
    });
  };

  /**
   * 动态生成产品的规格版本+套餐菜单数据，是否是父子级类型、规格属性分类、可自定义静态配置等
   * 默认规格版本为 || 前面， 代码声明产品类型 || 后面为属性版本（例如：颜色，内存版本等）
   * productDetail.VersionConfig:
   * - type: group(父子级) | item（普通版）
   * - groupType: normal(非父子级类型) | specification(规格) | attribute(属性) 配置在静态config.data中
   * - versionIndex: 0 / 1;此产品的属性/规格Index
   * - isSpecificationGroup | isAttributeGroup 根据静态VersionConfig自动生成判断
   * - groupList: 规格list versionName 规格名,tagInfosGroup 规格标签, cids 套餐id子数组, list {套餐具体数据, commodityName:|| 后的名称}, isOutOfStock 内部套餐是否都无库存了
   * - versionTitle
   * */
  @action
  initProductMenuVersion = (productDetail) => {
    const LANG = this.LANG;
    const {
      country: { code },
    } = this.root.area;
    const staticVersionConfig = getVersionConfig(
      productDetail.id,
      LANG,
      code,
      productDetail.commodities?.map?.((o) => o?.id)
    );

    // 套餐名是否包含双竖线
    const isGroup = productDetail.commodities.some((d) => d.info?.name?.includes('||'));
    // X3避免上线出bug兼容一下，之后会删掉。
    const isX3notAttrName =
      productDetail.id === 19 && productDetail.commodities.some((d) => d?.versionName?.includes('128'));
    // 属性在后，规格在前，取名称判断的位置需根据配置
    const isAttribute = staticVersionConfig.groupType === 'attribute';
    const VersionConfig = {
      type: isGroup ? 'group' : 'item',
      groupType: isGroup ? 'specification' : 'normal',
      versionIndex: isAttribute ? (isX3notAttrName ? 0 : 1) : 0,
      isSpecificationGroup: !isAttribute && isGroup,
      isAttributeGroup: isAttribute && isGroup,
      groupList: [],
      attrList: [], // 属性列表
      ...staticVersionConfig, // 静态配置可覆盖
    };

    const isSame = (name1, name2) => {
      let _name1 = name1?.replace(/\s/g, '').toLowerCase();
      let _name2 = name2?.replace(/\s/g, '').toLowerCase();
      return _name1 === _name2;
    };

    if (isGroup) {
      productDetail.commodities.forEach((commodity) => {
        const versionName = formatProductName(commodity.info.name, true).versionSplit;
        // 通过versionName取得多个属性或者规格
        for (let i = 1; i < versionName.length; i++) {
          // 处理套餐
          if (isAttribute && !isX3notAttrName) {
            commodity.versionName = versionName;
            // 套餐名默认取第一个
            commodity.commodityName = versionName[0];
          }

          // 属性可能有多个，规格只有一个。 attrList给每个属性列表分组，每一项都是一个属性列表，用于之后页面遍历
          const groupItem = isAttribute
            ? VersionConfig.attrList?.[i - 1]?.find((d) => isSame(d.versionName, versionName[i]))
            : VersionConfig.groupList.find((d) => isSame(d.versionName, versionName[VersionConfig.versionIndex]));
          if (!groupItem) {
            const listItem = {
              versionName: versionName[isAttribute && !isX3notAttrName ? i : VersionConfig.versionIndex],
              tagInfosGroup: [],
              cids: [commodity.id],
              list: [commodity],
            };

            // 通过内部名判断扩展规格/属性特殊配置，用于添加样式
            if (VersionConfig.groupStyle) {
              Object.keys(VersionConfig.groupStyle).forEach((key) => {
                if (isSameProductName(commodity.name, key)) {
                  listItem.groupStyle = VersionConfig.groupStyle[key];
                }
              });
            }
            // GO3 colorIndex 为颜色下标
            if (VersionConfig.colorIndex && VersionConfig.colorIndex !== i) {
              listItem.groupStyle = '';
            }
            if (!VersionConfig.attrList[i - 1]) {
              VersionConfig.attrList[i - 1] = [];
            }
            VersionConfig.attrList[i - 1].push(listItem);
            VersionConfig.groupList.push(listItem);
          } else {
            groupItem.cids.push(commodity.id);
            groupItem.list.push(commodity);
          }
        }
      });

      // go3内存由小到大排序
      if (isAttribute && VersionConfig.attrList.length > 1) {
        const getFirshNumbers = (str) => {
          return str.match(/\d+/g)?.[0];
        };
        VersionConfig.attrList[VersionConfig.attrList.length - 1].sort(
          (a, b) => getFirshNumbers(a.versionName) - getFirshNumbers(b.versionName)
        );
      }

      // 判断状态+规格标签处理
      VersionConfig.groupList.forEach((group) => {
        if (group.list.every((d) => d.isOutOfStock)) {
          group.isOutOfStock = true;
          // 如果缺货，规格属性不展示缺货标签
          // group.tagInfosGroup.push(LABEL.outOfStock);
          return; // 已经有了缺货标签就不需要其他标签，否则移动端样式错误
        }
        group.tagInfosGroup.push(...getVersionLabel(group.cids, this.LANG));
      });
    }
    productDetail.VersionConfig = VersionConfig;
    this.productDetail = productDetail;
  };

  /**
   * 根据 套餐名称的 || 处理套餐数据，增加productDetail数据：relativeCommodity
   * relativeCommodity 数据结构{name:||前共用套餐名称, cids:[xx,xx],diff:[差异],lists:[{套餐具体数据,difference:|| 后的差异，取最末尾的}] }
   * */
  @action
  initRelativeCommodity = (productDetail) => {
    const commodities = productDetail.commodities;
    let result = [];

    let did_cids = []; // 已经被整合过的cid序列，用于排除

    // 传入带||的名字，返回套餐名和最末尾的区别（内存）数组
    function handleName(name) {
      const arr = name.split('||');
      if (arr.length === 1) {
        return name && [arr[0].trim()];
      } else {
        return name && [arr[0].trim(), arr[arr.length - 1].trim()];
      }
    }

    let allDisabled = []; // 当前分类下所有套餐是否都处于不可售状态

    commodities.forEach((commodity) => {
      if (!did_cids.includes(commodity.id)) {
        const cname = handleName(commodity.info && commodity.info.name);
        const diff = [];
        // 同套餐名的差异数据集合
        const lists = commodities
          .map((item) => {
            const diff_name = handleName(item.info && item.info.name);
            if (diff_name[0] === cname[0]) {
              diff.push(diff_name[1]);
              return { ...item, difference: diff_name[1] || '' };
            }
            return false;
          })
          .filter((d) => !!d);
        const cids = lists.map((d) => d.id);
        did_cids = [...did_cids, ...cids];
        allDisabled = new Array(lists.length).fill(false);
        result.push({
          name: cname[0],
          cids,
          diff,
          lists,
        });
      }
    });
    allDisabled = allDisabled.map((o, index) => {
      const allCommodityDisabled = result.map((item) => {
        const cur = item.lists[index];
        return cur?.stock <= 0 || cur?.sale_state <= -1;
      });
      return allCommodityDisabled.every((status) => status === true);
    });
    result.forEach((o) => (o.allDisabled = allDisabled));
    productDetail.relativeCommodity = result;
  };

  @action
  fetchProductRecommend = (productId) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/getCommodityRecommendationInfo';
    return httpRequest({
      url,
      params: {
        productId,
      },
    })
      .then(({ code, data }) => {
        if (code === 0) {
          const { productCommodityInfo, productCommodityRecommendation } = data;
          if (productCommodityRecommendation.length > 0) {
            productCommodityRecommendation.forEach((i) => {
              i.commodities = [];
              i?.commodityRecommendationIds?.forEach((cid) => {
                const item = productCommodityInfo.filter((o) => o.id === cid)[0];
                if (item && item.enabled && item.sale_state >= -1 && item.price) {
                  item.isOutOfStock = item.sale_state < 0 || item.stock <= 0;
                  item.isNeedHidePrice = isNeedHidePriceProduct(item.product, this.country.code);

                  // 缺货商城价只显示原价
                  if (item.isOutOfStock && item?.origin_price) {
                    item.price = item.origin_price;
                    delete item.origin_price;
                  }
                  i.commodities.push(item);
                }
              });
              i.commodities = [
                ...i.commodities.filter((o) => !o.isOutOfStock),
                ...i.commodities.filter((o) => o.isOutOfStock),
              ];
            });
            this.productCommodityRecommend = productCommodityRecommendation;
          } else {
            this.productCommodityRecommend = [];
          }
        }
      })
      .catch(() => {
        this.productCommodityRecommend = [];
      });
  };

  /**
   * 查询产品的配件适配性并将accessoryCompatibilities属性存储于productDetail中
   * accessoryCompatibilities：内部包括产品对应分类和分类下配件信息，compatibilities属性存储配件对应的适配性
   * @return {Promise}
   */
  @action
  fetchAccessoryCompatibilities = (productId) => {
    const url = SERVER.API_SERVER_PRODUCT + '/getProductAccessoryCompatibility';
    return httpRequest({
      url,
      params: { productId },
    }).then((res = {}) => {
      const { code, data } = res;
      if (code === 0) {
        const {
          productAccessoryCompatibilities: { accessoryCompatibilities = [], accessoryCompatibilityBinds },
        } = data;
        if (!Array.isArray(accessoryCompatibilities)) return;
        // accessoryCompatibilities:产品对应的分类和分类下的配件信息，accessoryCompatibilityBinds:配件对应的镜头模组适配性
        // 遍历分类中的配件数据，并且填充是否兼容的数据
        accessoryCompatibilities.forEach((category) => {
          const { compatibilityCommodityInfos = [] } = category;
          category.select = true; // 默认所有的分类全部展开
          compatibilityCommodityInfos.forEach((item) => {
            const bindItems = accessoryCompatibilityBinds.filter((d) => d.accessoryCommodityId === item.commodityId);
            const compatibilities = {};
            bindItems?.forEach((d) => (compatibilities[d.compatibilityCommodityId] = d.bindResult));
            item.hasBindItems = bindItems.length > 0; // 该配件是否有配置兼容性信息，如果没有的话在“配件页”需隐藏
            item.compatibilities = compatibilities;
            item.select = false; // 默认所有子项都不选择，等到页面上再根据config选择初始数据
          });
        });

        this.productDetail.accessoryCompatibilities = accessoryCompatibilities;
      }
    });
  };

  @action
  fetchBanners = ({ platform }) =>
    new Promise((resolve) => {
      const { bannersMap } = this;
      if (bannersMap[platform]['loaded']) {
        resolve(true);
        return;
      }
      const url = SERVER.API_SERVER_META + '/banner/getBanner?app=' + platform;
      httpRequest({
        url,
        method: 'get',
      }).then((res) => {
        if (res.code !== 0) return;
        let banners = [];
        if (res.data.banner.length > 0) {
          res.data.banner.forEach((i) => i.info_list.forEach((j) => (j.banner_name = i.banner_name)));
          banners = res.data.banner.map((i) => i.info_list.map((j) => j)).reduce((a, b) => a.concat(b));
        }
        bannersMap[platform]['list'] = banners[0] ? banners : null;
        bannersMap[platform]['loaded'] = true;
        this.setStateValue('bannersMap', { ...bannersMap });
        resolve(true);
      });
    });

  /**
   * 查询产品的活动赠品和分销赠品
   */
  @action
  getProductGifts = (values, cb) => {
    const url = SERVER.API_SERVER_PRODUCT + '/listProductGifts';
    // const pid = values?.productId;
    httpRequest({
      url: url,
      params: values,
    }).then((res = {}) => {
      const { data } = res;
      const resellerGiftsList = []; // reseller gift弹窗使用
      const productGiftsMap = {};
      if (data) {
        // 处理赠品数据，分销赠品和活动赠品相同以活动赠品为准
        data.activityGifts?.forEach((item) => {
          productGiftsMap[item.commodityId] = {};
          productGiftsMap[item.commodityId].activityGift = item.gifts;
        });

        data.resellerGifts?.forEach((item) => {
          if (!productGiftsMap[item.commodityId]) {
            productGiftsMap[item.commodityId] = {};
          }

          productGiftsMap[item.commodityId].resellerGift = item.gifts.filter(
            (reseller_gift) =>
              !item.activityGift?.some((activity_gift) => activity_gift.commodityId === reseller_gift.commodityId)
          );

          productGiftsMap[item.commodityId].resellerGift?.forEach((d) => {
            if (resellerGiftsList.every((o) => o.commodityId !== d.commodityId)) {
              resellerGiftsList.push(d);
            }
          });
        });
      }
      this.resellerGiftsList = resellerGiftsList;
      this.productGiftsMap = productGiftsMap;
      cb && cb(resellerGiftsList);
    });
  };

  /**
   * 查询套餐赠品
   */
  @action
  getCommodityGifts = (cids) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/getEventPageCommodityGift';
    httpRequestLimitNums(
      {
        url: url,
        method: 'post',
        data: { commodityIdList: cids },
      },
      'commodityIdList',
      5
    )
      .then((res = {}) => {
        if (res.code !== 0) return;
        let data = res.data?.eventPageDetails;
        const commodityGiftsMap = {};
        if (data) {
          data.forEach((c) => {
            commodityGiftsMap[c?.commodityId] = { activityGifts: [], resellerGifts: [] };
            commodityGiftsMap[c?.commodityId].activityGifts =
              c?.activityGifts?.map((el) => {
                return { ...el, commodityName: el.commodityName };
              }) ?? [];
            // 分销赠品和活动赠品相同以活动赠品为准
            commodityGiftsMap[c?.commodityId].resellerGifts =
              c?.resellerGifts
                ?.filter(
                  (el) => !c.activityGifts?.some((activity_gift) => activity_gift.commodityId === el.commodityId)
                )
                .map((el) => {
                  return { ...el, commodityName: el.commodityName };
                }) ?? [];
          });
        }
        this.commodityGiftsMap = commodityGiftsMap;
      })
      .catch((err) => {
        armsErrorSend(err, { filename: 'x3mtc' });
      });
  };

  /**
   * 查询套餐的分销优惠
   */
  @action
  getCommodityAffiliateDiscount = (cids) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/getEventPageCommodityDiscount';
    return httpRequestLimitNums(
      {
        url: url,
        method: 'post',
        data: { commodityIdList: cids },
      },
      'commodityIdList',
      5
    ).then((res = {}) => {
      if (res.code !== 0) return;
      res.data = res.data.eventPageDetails;
      return res;
    });
  };

  @action
  subscribeArrivalNotice = (values, cb) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/subscribeArrivalNotice';
    httpRequest({
      url,
      method: 'post',
      data: values,
    }).then(() => {
      cb && cb();
    });
  };

  @action
  getCommodityInfo = (commodityId, cb) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/getInfo';
    httpRequest({
      url,
      params: {
        commodityId,
      },
    }).then((res) => {
      cb && cb(res);
    });
  };

  @action
  getSingleCommodityInfo = (props) => {
    const { cid, allowOutOfStock = true } = props;

    return new Promise((resolve, reject) => {
      if (isEmpty(cid)) resolve(null);

      httpRequest({
        url: SERVER.API_SERVER_PRODUCT + '/commodity/getInfo',
        params: { commodityId: cid },
      })
        .then((res) => {
          const commodity = res?.data?.commodity;
          if (res.code !== 0 || !commodity || commodity.enabled === false || commodity.sale_state <= -2) {
            reject({ code: res.code, message: '获取套餐信息失败' });
          }
          if (!allowOutOfStock) {
            if (commodity.sale_state === -1) {
              reject({ code: res.code, message: '获取套餐信息成功，但缺货过滤' });
            }
          }
          resolve(commodity);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  @action
  getCampainInfos = ({ ids = [], campaign_key = '' }) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/getCampainInfos';
    if (!ids.length || !campaign_key) return;
    return httpRequestLimitNums(
      {
        url,
        method: 'post',
        data: {
          commodityIds: ids,
          campaign: campaign_key,
        },
      },
      'commodityIds'
    );
  };

  @action
  getCommodityPrices = (commodity_ids, cb) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/getInfos';
    return httpRequestLimitNums({
      url,
      method: 'post',
      data: { commodity_ids },
    })
      .then((res) => {
        if (res.code === 0) {
          res.data.commodities.forEach((commodity) => {
            commodity?.displays?.forEach((display) => {
              display.productId = commodity.product;
            });
            commodity.isOutOfStock = !isAvailableForSale(commodity);
            commodity.isNeedHidePrice = isNeedHidePriceProduct(commodity.product, this.country.code);

            // 缺货套餐只显示原价
            if (commodity.isOutOfStock && commodity?.origin_price) {
              commodity.price = commodity.origin_price;
              delete commodity.origin_price;
            }
          });
          res.data.commodities = res.data.commodities.filter((o) => {
            const c1 = o.sale_state !== -2 && o.price && o.enabled && o.displays;
            const c2 = o.sale_state !== -2 && o.price && o.enabled && this.careplus_cost_cids?.includes(o.id);
            return c1 || c2;
          });
          cb && cb(res);
        }
        return res;
      })
      .catch(() => {});
  };

  @action
  getCommodityPricesTemp = (commodity_ids, cb) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/getInfos';

    return httpRequest({
      url,
      method: 'post',
      data: { commodity_ids },
    })
      .then((res) => {
        if (res.code === 0) {
          res.data.commodities.forEach((commodity) => {
            commodity?.displays?.forEach((display) => {
              display.productId = commodity.product;
            });
            commodity.isOutOfStock = !isAvailableForSale(commodity);
            commodity.isNeedHidePrice = isNeedHidePriceProduct(commodity.product, this.country.code);

            // 缺货套餐只显示原价
            if (commodity.isOutOfStock && commodity?.origin_price) {
              commodity.price = commodity.origin_price;
              delete commodity.origin_price;
            }
          });
          res.data.commodities = res.data.commodities.filter(
            (o) => o.sale_state !== -2 && o.price && o.enabled && o.displays
          );
          cb && cb(res);
        }
        return res;
      })
      .catch(() => {});
  };

  /**
   * 处理包装清单、产品参数、注意事项数据，包装清单包括两种类型，type:1相机类型，2:配件类型
   * @param {object} params {productId} 需要获取包装清单的产品id或其他数据
   */
  @action
  getProductDiff = (params) => {
    const url = SERVER.API_SERVER_PRODUCT + '/commodity/getDifferences';
    return httpRequest({
      url,
      params,
    }).then((res = {}) => {
      try {
        const { code, data } = res;
        if (code === 0 && !isEmpty(data)) {
          // cb && cb(data);
          const {
            packing_list_points,
            commodity_differences,
            productPackListType,
            accessoriesPackListText,
            precautionsText = {},
            productParameterInfos = [],
          } = data;

          // 注意事项
          if (!isEmpty(precautionsText?.precautionsContentText, true)) {
            this.productImportantNotes = {
              type: precautionsText.styleType,
              data: precautionsText.precautionsContentText,
            };
          }

          // 技术参数-配件用
          this.productParameterInfos = productParameterInfos;

          // 包装清单
          this.packingListPoints.productPackListType = productPackListType;
          if (productPackListType === 2) {
            this.packingListPoints.accessoryDiffData = accessoriesPackListText;
          } else {
            this.initProductDiffData('packingListPoints', packing_list_points, commodity_differences);
          }
        }
        return res;
      } catch (e) {
        console.error(e);
      }
    });
  };

  /** 相机类型包装清单数据处理 */
  @action
  initProductDiffData = (key, points, commodityDifferences) => {
    const { X_Language } = this.root.area.country;
    if (isEmpty(points) || isEmpty(commodityDifferences)) return;
    let commodities = commodityDifferences.filter((o) => o.differences?.length > 0);
    commodities = commodities?.sort((a, b) => a.differences[0]?.order_index - b.differences[0]?.order_index);
    const maxSelect = isMobile() ? 2 : 3;
    if (!['CN', 'JP'].includes(CountryCode)) {
      // ONEX2 火影版本
      points = points?.filter((o) => ![58, 59].includes(o.id));
      commodities = commodities?.filter((o) => ![1168, 1174].includes(o.id));
    }

    points.forEach((point) => {
      // 自定义套餐列数据主图或者其他特殊配置
      if (customColConfig?.[point.id]?.[X_Language]) {
        point.image_url = customColConfig[point.id]?.[X_Language]?.masterPic;
      }
    });

    commodities.forEach((commodity, k) => {
      commodity.diff = [];

      commodity.differences.forEach((diffData) => {
        const point = points.find((p) => p.id === diffData.point_id);
        if (point) point.show = true;
        diffData.point = point || {};
        commodity.diff.push(diffData);
      });

      // 自定义套餐行数据主图或者其他特殊配置
      if (customRowConfig?.[commodity.id]?.[X_Language]) {
        commodity.displays[0].url = customRowConfig[commodity.id]?.[X_Language]?.masterPic;
      }

      if (k < maxSelect) {
        if (k === 0) {
          commodity.position = 'left';
        } else if (k === 1) {
          commodity.position = 'right';
        }
        commodity.checked = true;
      }
      commodity.checked = k < maxSelect;
    });

    points = points.filter((p) => p.show);

    this[key].maxSelect = maxSelect;
    this[key].pointGroup = points;
    this[key].commodityGroup = commodities;
  };

  /** 将自定义套餐弹窗中的临时信息加入自定义套餐curPersonalizedItems中 */
  @action
  addDiyTempCartToDiyCart = (tempCartList, cb) => {
    // const PersonalizedList = [
    //   {
    //     pid:1,
    //     createTime:new Date().getTime(),
    //     endTime:new Date().getTime() + 24 * 60 * 60 * 1000,
    //     CountryCode:'CN',
    //     commodities:[]
    //   },
    // ]
    const {
      productDetail: { id: pid },
    } = this;
    const tempItem = {
      pid,
      CountryCode,
      createTime: new Date().getTime(),
      endTime: new Date().getTime() + 24 * 60 * 60 * 1000,
      commodities: tempCartList,
    };

    this.personalizedData = [tempItem, ...this.personalizedData.filter((d) => d.pid !== tempItem.pid)];
    // 当前选中套餐同自定义套餐中选中的主相机套餐
    const curSelect_cid = tempCartList.find((d) => d.type === 'cameras')?.id;
    this.setStateValue('curSelectCommodityId', curSelect_cid);
    this.setStateValue('isSelectPersonalized', true);
    cache.set('personalized_data', JSON.stringify(this.personalizedData));
    cb && cb();
  };

  @action
  fetchExtraBindService = (commodityList) => {
    const { country } = this.root.area;
    let extraServiceCids = [];
    commodityList?.forEach?.((commodity) => {
      // 增值服务直接保存id
      if (commodity.bind_service) {
        extraServiceCids.push(commodity.id);
      } else {
        commodity.bindClimbServices?.forEach((service) => {
          if (service.serviceCommodityId) {
            extraServiceCids.push(service.serviceCommodityId);
          }
        });
      }
    });
    extraServiceCids = Array.from(new Set(extraServiceCids));
    const careplusCids = ExtraBindServiceConfig.getCarePlusCostCids(extraServiceCids);
    /** 云服务也使用增值服务查询逻辑 */
    extraServiceCids = Array.from(new Set([...extraServiceCids, ...careplusCids, getCloudCid()]));
    if (!extraServiceCids[0]) return null;
    // 获取增值服务列表及其活动价
    Promise.all([this.getCommodityPrices([...extraServiceCids])])
      .then(([resOrigin]) => {
        let commodities = resOrigin?.data?.commodities ?? [];
        const _carePlusPrice = new Map();
        commodities?.forEach((commodity) => {
          commodityList.forEach((cInfo) => {
            cInfo.bindClimbServices?.forEach?.((service) => {
              if (service?.serviceCommodityId === commodity.id) {
                commodity.serviceType = service?.serviceType;
              }
            });
          });
          if (this.careplus_cost_cids.includes(commodity?.id)) {
            const priceFormat = formatPriceThousands({
              price: commodity.price.amount,
              currency: commodity.price.currency,
              signal: commodity.price.signal,
              country,
            });
            const relatedCommodities =
              ExtraBindServiceConfig.DataMap.care_plus.group?.filter((o) => o.replaceCostCid === commodity?.id) ?? [];
            relatedCommodities.forEach((relatedCommodity) => {
              _carePlusPrice.set(relatedCommodity?.cid, priceFormat);
            });
          }
        });
        ExtraBindServiceConfig.setCommodityToMap(commodities);
        setCommodityToCloudConfig(commodities);
        this.setStateValue('carePlusPrice', _carePlusPrice);
        this.initProductExtraBindService();
      })
      .catch((e) => {
        armsErrorSend(e, { filename: '获取增值服务套餐信息失败' });
      });
  };

  // 获取当前选择套餐对应的虚拟服务选项
  @action
  initProductExtraBindService = (_productDetail) => {
    const { curSelectCommodityId, curSelectCommodity, extraService } = this;

    const productDetail = this.productDetail || _productDetail;

    // 不可售商品不展示虚拟服务选购
    if (
      isEmpty(productDetail) ||
      !curSelectCommodityId ||
      curSelectCommodity?.stock <= 0 ||
      curSelectCommodity?.sale_state <= -1
    ) {
      this.setStateValue('extraBindService', []);
      this.setStateValue('extraService', []);
      return;
    }
    const extraServiceCids =
      curSelectCommodity.bindClimbServices?.map?.((service) => service.serviceCommodityId).filter((o) => !!o) || [];
    let extraBindService = ExtraBindServiceConfig.getProductExtraBindService(
      extraServiceCids,
      this.curSelectCommodityCount
    );

    const { Q4BFGiftsCareForUS } = this.root.app.campaignWrap.campaignWrap;
    extraBindService = Q4BFGiftsCareForUS.hideCareFromProductPage({ extraBindServiceList: extraBindService });

    const redirectData = ThirdPartyPlatformConfig.getRedirectData({ pid: productDetail.id, cid: curSelectCommodityId });

    // 云服务如果已经选购了，切换套餐时，保留之前的数量
    const cloudProductInfo = getCloudProductConfig();
    const cloudCommodity = extraService?.find((o) => o?.extraType === 'cloud_services');

    if (cloudCommodity?.commodity?.count <= 1) {
      cloudProductInfo.commodity.count = cloudCommodity?.commodity?.count;
    }

    // 如果是非云存相关商品，这里设置成空，否则会导致页面上认为存在虚拟服务，导致展示样式css有间隔问题。
    this.setStateValue(
      'extraService',
      cloudProductConfig.relatedProductIds.includes(productDetail.id) ? [cloudProductInfo] : []
    );
    this.setStateValue('extraBindService', redirectData.url ? [] : extraBindService);
  };

  @action
  setExtraBindServiceCount = (count) => {
    const { extraBindService } = this;
    extraBindService.forEach((o) => {
      // 只修改已选中的增值服务数量。
      if (o.commodity.count > 0) o.commodity.count = count;
    });
    this.setStateValue('extraBindService', extraBindService);
  };

  @action
  addServiceListToItem = (list, cb = () => {}) => {
    const cartItems = this.root.order.allCartItems;
    list.forEach(({ item, service }) => {
      const cartItem = cartItems.filter((o) => o.commodityId === item.commodityId)[0];
      const serviceItem = {
        id: service.id,
        number: item.count,
        type: service.serviceType,
        ...service,
      };
      delete serviceItem.commodity;
      if (!cartItem?.bind_services?.length) {
        cartItem.bind_services = [{ ...serviceItem }];
      } else {
        cartItem.bind_services.push(serviceItem);
      }
      cartItem.bind_services = uniq(cartItem.bind_services, 'id');
    });

    this.root.order.setCartItems(cartItems, true);
    this.root.order.getCartItemsDetail().then(() => {
      cb && cb();
    });
  };

  // 获取云服务商品的数据，只获取主套餐的信息
  @action
  fetchCloudServiceInfo = (cid) => {
    return this.getCommodityPrices([cid || cloudProductConfig.cid]).then((res) => {
      if (res.code === 0) {
        setCommodityToCloudConfig(res?.data?.commodities);
      }
      return res?.data?.commodities;
    });
  };

  /**
   * 产品页是否展示全景主图
   * 满足以下条件
   * 1. 产品id为26（X4）
   * 2. 命中abtest x4_product_page_360_pic compact1
   * 3. 语言为en_us
   */
  @computed
  get isPanoramicPage() {
    const { productDetail } = this;
    // return true;
    return panoramicDisplayConfig.panoramicDisplayPid?.includes(productDetail?.id);
    // return (
    //   productDetail?.id === 26 && isTargetingResult('x4_product_page_360_pic', 'compact1') && X_Language === 'en_us'
    // );
  }

  /**
   * 获取当前支持的产品的相关推荐配件信息
   * 命中abtest才返回
   */
  @computed
  get relatedAccessoryConfig() {
    const { isTargetingResult } = this.root.abtest;
    const {
      productDetail: { id: pid },
    } = this;

    const hitABTest = isTargetingResult('product_page_related_accessory', 'related-accessory');

    return hitABTest ? supportedAdapterIdMap[pid] : undefined;
  }

  // 产品页临时购物车加入正式购物车
  @action
  addProductsToCart = () => {
    const {
      productDetail: { id: pid },
      curSelectCommodity,
      curSelectCommodityCount,
      isSelectPersonalized,
      productCarts,
      InsuranceService,
    } = this;
    const { id: cid } = curSelectCommodity;

    let type = 'not-once';
    let carts = productCarts;
    // 处理导流产品
    const redirectData = ThirdPartyPlatformConfig.getRedirectData({ pid, cid });
    if (redirectData?.url) {
      window.open(redirectData.url, '_blank');
      return;
    }

    // 8K直播软件 || 预售产品直接进入结账页，仅允许单独购买
    if (pid === _8kLivePid || isPreSaleCommodity({ cid })) {
      type = 'once';
    }

    // 绑定服务单独购买(GO2定制，Flow定制除外), 显示序列号验证弹窗
    const filterBindService = [...CustomSkinHelper.CustomSkinPidList].includes(curSelectCommodity.product);
    if (curSelectCommodity.bind_service && !filterBindService) {
      // 虚拟服务弹出序列号弹窗 或者直接购买
      type = 'once';
      if (!InsuranceService.serialChecked) {
        this.handleInsuranceService('toggleModal', true);
        return;
      }
      this.handleInsuranceService('successCheckout');
    }

    // 记录当前套餐选择的T恤赠品SKU
    const { GiftTShirt } = this.root.app.campaignWrap.campaignWrap;
    GiftTShirt.setProductSelectSizeCache({ curSelectCommodity });

    // 当前产品为非绑定服务套餐加购
    if (!curSelectCommodity.bind_service) {
      const isCustomSkin = (cid) => {
        return [...CustomSkinHelper.CustomSkinCidList].includes(cid);
      };
      // 过滤出非绑定服务类型产品（GO2/Flow定制除外），用于直接加入购物车
      carts = productCarts.filter((o) => !o.bind_service || isCustomSkin(o.id));
      // 过滤出绑定服务类型产品，且为非GO2/Flow定制，用于绑定主产品父子关系
      const bindServiceList = productCarts.filter((o) => o.bind_service && !isCustomSkin(o.id));
      carts.forEach((o) => {
        if (o.groupType === 'main') {
          o.bind_services = bindServiceList.map((service) => ({
            id: service.id,
            type: service.serviceType,
            number: o.count,
            price: service.price,
            origin_price: service.origin_price,
            product_name: service.product_name,
            commodity_name: service?.info?.name,
          }));
        }
      });
    }
    const timeStamp = new Date().getTime();

    // 如果是GO3S + 定制贴购买，需要记录定制贴对应的颜色（黑/白）
    CustomSkinHelper.handleSetGO3SAddToCartColor({ commodity: curSelectCommodity });

    const ECTrackerData = (() => {
      const _items = [];
      carts.forEach((item) => {
        _items.push({
          ...item,
          count: isSelectPersonalized ? curSelectCommodityCount : item.count,
        });
        item.bind_services?.forEach((service) => {
          const count = isSelectPersonalized ? curSelectCommodityCount : service.number;
          _items.push({
            ...service,
            number: count,
            count,
          });
        });
      });
      return _items;
    })();

    EcommerceTracker.addToCartFromProductPage({ items: ECTrackerData });
    EcommerceTracker.addToCartToNaver();
    this.root.app.trackBaiduAdv({ newType: 46 });

    carts.forEach((o, i) => {
      const item = {
        product: {
          id: o.product,
          product_name: o.product_name,
          info: o.info,
        },
        commodity: o,
        bindClimbServices: [],
        count: isSelectPersonalized ? curSelectCommodityCount : o.count,
        bind_services: o.bind_services,
      };
      if (o?.bindClimbServices?.length !== 0) {
        item.bindClimbServices = o.bindClimbServices;
      }
      if (isSelectPersonalized) {
        item.bind_node = {
          // OneR 绑定父子关系用
          type: o.type === 'cameras' ? 'parent' : 'child',
          timeStamp,
        };
      }
      CustomSkinHelper.handleAddedToCart({ commodityId: o.id, mobxOrder: this.root.order });
      if (item.count > 0) {
        this.root.order.addToCart(item, type, () => {}, true, false, 'productPage');
        EcommerceTracker.addToCartToUA({ items: [item] });
        // ThirdPartyTracker.linkedinTrack('addToCart');
      }
      if (i === carts.length - 1) {
        let cartPath = '/cart';

        if (this.relatedAccessoryConfig) {
          cartPath = `/related-accessories?product=${this.relatedAccessoryConfig.urlKey}`;
          stashCidInSessionStorage({ cid });
        }
        browserPushUrl(type === 'not-once' ? cartPath : '/order/confirm');
      }
    });
  };

  @action
  handleInsuranceService = (type, data) => {
    const { InsuranceService } = this;
    switch (type) {
      // 输入 序列号校验弹窗的序列号填空 的序列号
      case 'inputSerial':
        runInAction(() => {
          InsuranceService.serial = data;
          InsuranceService.serialCheckedMsg = '';
        });
        break;
      // 输入购买日期
      case 'inputDate':
        runInAction(() => {
          InsuranceService.purchaseTime = data?.valueOf();
          InsuranceService.serialCheckedMsg = '';
        });
        break;
      // 是否勾选协议
      case 'selectProtocol':
        InsuranceService.selectProtocol = data;
        break;
      // 提交序列号弹窗，check+跳转结账页
      case 'submit':
        data = {
          commodity_id: this.curSelectCommodityId,
          device_serial: InsuranceService.serial,
          purchaseTime: isEmpty(InsuranceService.purchaseTime) ? undefined : InsuranceService.purchaseTime,
        };

        ProductTracker.productPageBindServiceSN({
          action: InsuranceService.stepStack.length === 1 ? 'submit' : 'step2_submit',
          productState: this,
        });
        this.checkSerialInsuranceService(data);
        return;
      // 校验成功，将序列号数据增加到cache
      case 'checkSuccess':
        runInAction(() => {
          InsuranceService.serialCheckedMsg = '';
          InsuranceService.modalVisible = false;
          InsuranceService.serialChecked = true;
        });
        cache.set('i_s_d_s', InsuranceService.serial);

        // 如果check成功的sn码不属于超期购买的，则删除超期购买的缓存，确保结账页只有超期的care才有对应的careExtendInfo
        const _careExtendInfo = this.handleInsuranceService('getCareExtendInfo');
        if (_careExtendInfo?.sn !== InsuranceService.serial) {
          cache.remove('care_extend_info');
        }

        return;
      // 校验失败处理
      case 'checkErr':
        runInAction(() => {
          InsuranceService.serialCheckedMsg = data;
          InsuranceService.serialChecked = false;
          InsuranceService.selectProtocol = false;
        });

        return;
      // 重置
      case 'reset':
        runInAction(() => {
          InsuranceService.modalVisible = false;
          InsuranceService.serialChecked = false;
          InsuranceService.selectProtocol = false;
          InsuranceService.serialCheckedMsg = '';
          InsuranceService.purchaseTime = '';
          InsuranceService.stepStack = [1];
        });

        break;
      // input foucus事件回调
      case 'inputFocus':
        ProductTracker.productPageBindServiceSN({
          action: 'focus',
          productState: this,
        });
        break;
      // 打开 / 关闭序列号弹窗
      case 'toggleModal':
        runInAction(() => {
          InsuranceService.modalVisible = data;
          InsuranceService.serialCheckedMsg = '';
          InsuranceService.purchaseTime = '';
          InsuranceService.stepStack = [1];
        });

        ProductTracker.productPageBindServiceSN({
          action: data === true ? 'enter' : 'close',
          productState: this,
        });
        break;
      // 验证成功跳转购买页后删除一些信息
      case 'successCheckout':
        runInAction(() => {
          InsuranceService.serialChecked = false;
          InsuranceService.selectProtocol = false;
          InsuranceService.purchaseTime = '';
        });

        break;
      // 弹窗步骤变化
      case 'onStepChange':
        if (data) {
          InsuranceService.stepStack.push(data); // 下一步
        } else {
          runInAction(() => {
            InsuranceService.stepStack.pop(); // 回到上一步
            InsuranceService.selectProtocol = false;
            InsuranceService.purchaseTime = '';
            InsuranceService.serialCheckedMsg = '';
          });
        }
        ProductTracker.productPageBindServiceSN({
          action: data === 2 ? 'date' : 'back_step1',
          productState: this,
        });
        break;
      // 从本地缓存里获取care超期的信息
      case 'getCareExtendInfo':
        const careExtendInfo = cache.get('care_extend_info');
        return JSON.parse(careExtendInfo) || {};
      default:
    }
  };

  @action
  checkSerialInsuranceService = debounce((data) => {
    const LANG = this.LANG;

    const handleSuccess = () => {
      this.handleInsuranceService('checkSuccess');
      this.addProductsToCart();
    };

    const handleErrMsg = (code) => {
      let pass = false;
      let errMsg = '';
      switch (code) {
        case 'empty':
          errMsg = LANG['bindServices.verifySerialModal.inputMsgEmpty'];
          break;
        case 3001:
        case 50001:
        case 50002: // 序列号不存在/不是相机
        case 50003:
        case 50009: // 服务套餐绑定不存在
        case 12535:
          errMsg = LANG['bindServices.verifySerialModal.inputMsgNotExist'];
          break;
        // 超过绑定时间，但是从允许超期购买的链接跳转过来的除外
        case 50004:
          const careExtendInfo = this.handleInsuranceService('getCareExtendInfo');
          if (careExtendInfo?.sn === data.device_serial && isDuringNowAfterDate(careExtendInfo.t, 3)) {
            pass = true;
          } else {
            errMsg = LANG['bindServices.verifySerialModal.inputMsgNotExist'];
          }
          break;
        case 50005: // 已绑定care
          errMsg = LANG['bindServices.verifySerialModal.inputMsgBound'];
          break;
        case 50010: {
          // link超过购买时间
          const isCare = this.productDetail.id === 141;
          // link2超过购买时间
          const isLink2 = [3406, 3407].includes(this.curSelectCommodityId);
          const showErrMsg = isCare || isLink2;
          errMsg = showErrMsg
            ? LANG['bindServices.verifySerialModal.purchaseDateErrMsg']
            : LANG['bindServices.verifySerialModal.purchaseDateErrMsg_extend'];
          break;
        }
        case 50008: // 非商城购买的link or Flow
          this.handleInsuranceService('onStepChange', 2);
          return;
        case 50027: // 二手机增值服务购买限制
          errMsg = LANG['bindServices.verifySerialModal.secondHandDisable'];
          break;
        case 'minionsCareMsg':
          errMsg = LANG['temp.go2MinionsCareSerialMsg'];
          break;
        case 'minionsWarrantyMsg':
          errMsg = LANG['temp.go2MinionsWarrantySerialMsg'];
          break;
        default:
      }

      if (pass) {
        handleSuccess();
      } else {
        this.handleInsuranceService('checkErr', errMsg);
      }
    };

    if (!data.device_serial) {
      handleErrMsg('empty');
      return;
    }
    httpRequest({
      url: BASE_STORE_URL + '/insurance/service/checkSerial',
      method: 'post',
      data,
    }).then(({ code }) => {
      if (code === 0) {
        // 小黄人序列号不允许购买GO2care
        const reg = /^IG2KN[A-z0-9]{9}$/i;
        const serialTest = reg.test(data.device_serial);
        switch (data.commodity_id) {
          case 1468:
            if (serialTest) {
              return handleErrMsg('minionsCareMsg');
            }
            break;
          case 1426:
            if (serialTest) {
              return handleErrMsg('minionsWarrantyMsg');
            }
            break;
          default:
        }

        handleSuccess();
        return;
      }
      handleErrMsg(code);
    });
  }, 200);

  @action
  getWebsiteString = (params, cb) => {
    httpRequest({
      url: 'https://service.insta360.com/www-service/website/word/getString',
      params,
    }).then((res) => {
      cb && cb(res.data);
    });
  };

  @action
  fetchProductOverview = (params) => {
    return httpRequest({
      url: SERVER.API_SERVER_PRODUCT + '/getOverview',
      params,
    })
      .then(({ code, data = {} }) => {
        if (code !== 0 || !data.productOverviewTemplate) {
          throw new Error('fetchProductOverviewError');
        }
        this.setStateValue('productModularOverview', {
          fetchDone: true,
          ...data.productOverviewTemplate,
        });
      })
      .catch(() => {
        this.setStateValue('productModularOverview', { fetchDone: true });
      });
  };

  @action
  stableDiffusionText2Img = (data) => {
    return new Promise((resolve, reject) => {
      if (data.taskIndex >= 16) {
        reject(new Error('请求数量限制'));
        return;
      }
      axios({
        url: SERVER.API_SERVER_STABLE_DIFFUSION + '/stable/diffusion/aiPicture',
        method: 'POST',
        data,
      })
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  @action
  getAiPictureResult = ({ taskId, taskIndex }) => {
    return new Promise((resolve, reject) => {
      if (taskIndex >= 16) {
        reject(new Error('请求数量限制'));
        return;
      }
      axios({
        url: SERVER.API_SERVER_STABLE_DIFFUSION + `/stable/diffusion/getAiPictureResult?taskId=${taskId}`,
      })
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // x3x4的配件商品id
  x3Accessories = [];
  x4Accessories = [];

  @action
  getX3X4Accessories = async () => {
    try {
      if (this.x3Accessories.length === 0 && this.x4Accessories.length === 0) {
        const { x3Accessories, x4Accessories } = await getX3X4AccessoriesProductIds();
        this.x3Accessories = x3Accessories;
        this.x4Accessories = x4Accessories;
      }
    } catch (err) {
      console.error(err);
    }
  };

  hasX3X4Accessories = (productIds = []) => {
    const res = {
      x3: false,
      x4: false,
      both: false,
    };
    for (let i = 0; i < productIds.length; i++) {
      const x3 = this.x3Accessories.includes(productIds[i]);
      const x4 = this.x4Accessories.includes(productIds[i]);
      const both = x3 && x4;
      if (x3) res.x3 = true;
      if (x4) res.x4 = true;
      if (both) res.both = true;
    }

    if (!res.x3 && !res.x4) return undefined;

    return {
      ...res,
      target: res.both ? 'both' : res.x4 ? 'x4' : 'x3',
    };
  };
}

export default State;
